import { axios, transformAxiosError } from "@/utils/axios.create";
import { IAction } from "@/domain/actions.interface";

export type ActionsFindAllPayload = {
  search?: string;
  code?: string;
  status?: string;
  type?: string;
  pageNumber: number;
  pageSize: number;
  sortField: string;
  sortOrder: number;
};

export type ActionsFindAllResponse = {
  list: IAction[];
  totalCount: number;
};

export type ActionPayload = {
  name: string;
  code: string;
  status: string;
  description: string;
  type?: string;
  link?: string;
  order: number;
  icon?: string;
  subActions?: string[];
  createdBy?: string;
  updatedBy?: string;
};

const findAll = async ({
  search,
  code,
  status,
  type,
  pageNumber,
  pageSize,
  sortField,
  sortOrder,
}: ActionsFindAllPayload): Promise<ActionsFindAllResponse> => {
  const queryParams = new URLSearchParams({
    pageNumber: String(pageNumber + 1),
    pageSize: String(pageSize),
    sortField,
    sortOrder: sortOrder === 1 ? "asc" : "desc",
  });

  if (search) queryParams.append("search", search);
  if (code) queryParams.append("code", code);
  if (status) queryParams.append("status", status);
  if (type) queryParams.append("type", type);

  return axios
    .get(`/actions?${queryParams.toString()}`)
    .then((res) => {
      const body = res.data.body;
      return {
        list: (body?.list as IAction[]) || [],
        totalCount: (body?.totalCount as number) || 0,
      };
    })
    .catch((err) => {
      return Promise.reject(transformAxiosError(err));
    });
};

const findOne = async (id: string): Promise<IAction> => {
  return axios
    .get(`/actions/find/${id}`)
    .then((res) => {
      return res.data.body as IAction;
    })
    .catch((err) => {
      return Promise.reject(transformAxiosError(err));
    });
};

const create = async (data: ActionPayload): Promise<IAction> => {
  return axios
    .post("/actions", data)
    .then((res) => {
      return res.data.body as IAction;
    })
    .catch((err) => {
      return Promise.reject(transformAxiosError(err));
    });
};

const update = async ({
  id,
  data,
}: {
  id: string;
  data: ActionPayload;
}): Promise<IAction> => {
  return axios
    .put(`/actions/${id}`, data)
    .then((res) => {
      return res.data.body as IAction;
    })
    .catch((err) => {
      return Promise.reject(transformAxiosError(err));
    });
};

const archive = async (id: string): Promise<void> => {
  return axios
    .patch(`/actions/archive/${id}`)
    .then(() => {
      return;
    })
    .catch((err) => {
      return Promise.reject(transformAxiosError(err));
    });
};

export const ActionsService = {
  findAll,
  findOne,
  create,
  update,
  archive,
};
